import {FC, KeyboardEventHandler, useContext, useMemo, useState} from 'react';
import {FieldValues, RegisterOptions, useForm} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';

import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ClearIcon from '@mui/icons-material/Clear';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import SearchIcon from '@mui/icons-material/Search';
import {FormControl, InputLabel, MenuItem, Select} from '@mui/material';

import {EventBusContext} from '../../../providers/EventBus';

import {DictionaryService} from '../../../api/services/dictionary.service';

import {devicesPageable, devicesQueryTrigger, devicesState} from '../../../store/deviceModels/atoms';

interface ISearchBy {
  onKeyUp: KeyboardEventHandler<HTMLDivElement>;
  name: 'id' | 'operator' | 'serial' | 'modelName' | 'createdYear';
  label: string;
  additionalFunc?: (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  rules?: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  resetSearchField: () => void;
  onSubmit: () => Promise<unknown>;
}

interface ITableToolbar {
  loadingState: 'hasValue' | 'hasError' | 'loading';
}

export const TableToolbar: FC<ITableToolbar> = ({loadingState}) => {
  const pageable = useRecoilValue(devicesPageable);
  const triggerQuery = useSetRecoilState(devicesQueryTrigger);
  const setDevices = useSetRecoilState(devicesState);
  const [searchString, setSearchString] = useState({
    serial: '',
    operator: '',
    modelName: '',
    createdYear: '',
  });
  const [loading, setLoading] = useState<Record<keyof typeof searchString, boolean>>({
    serial: false,
    operator: false,
    modelName: false,
    createdYear: false,
  });
  const {bus} = useContext(EventBusContext);
  const [selectedSearch, setSelectedSearch] = useState<keyof typeof searchString>('serial');

  const {
    control,
    getValues,
    watch,
    setValue,
    formState: {errors},
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: searchString,
  });

  const watchData = watch();

  const resetSearchField = (entity: keyof typeof searchString) => () => {
    setValue(entity, '', {shouldValidate: true});
    setSearchString((prev) => ({
      ...prev,
      [entity]: '',
    }));
    clearErrors();
    triggerQuery((v) => v + 1);
  };

  const keyHandler: (entity: keyof typeof searchString) => KeyboardEventHandler<HTMLDivElement> = (entity) => (e) => {
    switch (e.code) {
      case 'Escape':
        if (!loading[entity] && watchData[selectedSearch]) {
          resetSearchField(entity)();
          onSubmit(entity);
        }
        return;
      case 'Enter':
        !errors[entity] && watchData[selectedSearch] && !loading[entity] && onSubmit(entity);
        return;
    }
  };

  const onSubmit = async (entity: keyof typeof searchString) => {
    const data = getValues();

    try {
      setLoading((prev) => ({
        ...prev,
        [entity]: true,
      }));

      const res = await DictionaryService.getAllDevices({
        spec:
          entity === 'createdYear' && data[entity]
            ? {
                [entity]: Number(data[entity].trim()),
              }
            : data[entity]
              ? {
                  [entity]: data[entity].trim(),
                }
              : {},
        pageable: {
          ...pageable,
          order: ['id', 'ASC'],
        },
      });

      setDevices(res.data);
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading((prev) => ({
        ...prev,
        [entity]: false,
      }));
    }
  };

  const SEARCH_BY: Record<keyof typeof searchString, ISearchBy> = useMemo(() => {
    return {
      serial: {
        onKeyUp: keyHandler('serial'),
        label: 'Поиск по заводскому номеру',
        name: 'serial',
        onSubmit: () => onSubmit('serial'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('serial');
        },
        resetSearchField: resetSearchField('serial'),
      },
      operator: {
        onKeyUp: keyHandler('operator'),
        label: 'Поиск по мобильному оператору',
        name: 'operator',
        onSubmit: () => onSubmit('operator'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('operator');
        },
        resetSearchField: resetSearchField('operator'),
      },
      modelName: {
        onKeyUp: keyHandler('modelName'),
        label: 'Поиск по названию модели',
        name: 'modelName',
        onSubmit: () => onSubmit('modelName'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('modelName');
        },
        resetSearchField: resetSearchField('modelName'),
      },
      createdYear: {
        onKeyUp: keyHandler('createdYear'),
        label: 'Поиск по году производства',
        name: 'createdYear',
        onSubmit: () => onSubmit('createdYear'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('createdYear');
        },
        rules: {
          pattern: {
            value: /^((\d)+)?()?$/,
            message: 'Только цифры',
          },
        },
        resetSearchField: resetSearchField('createdYear'),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyHandler]);

  return (
    <Toolbar
      sx={{
        pl: {sm: 2},
        pr: {xs: 1, sm: 1},
        justifyContent: 'space-between',
      }}
    >
      <Stack direction="column" width="100%" spacing={1}>
        <Stack direction="row" paddingY={2} justifyContent="space-between">
          <Stack direction="row" spacing={2}>
            <ControlledInput
              {...SEARCH_BY[selectedSearch]}
              control={control}
              size="small"
              fullWidth={true}
              sx={{width: 350, mt: '10px !important'}}
              InputProps={{
                endAdornment: (
                  <>
                    {watchData[selectedSearch] ? (
                      <Tooltip title="Очистить">
                        <IconButton size="small" onClick={SEARCH_BY[selectedSearch].resetSearchField}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <LoadingButton
                      sx={{padding: '8px', minWidth: '40px'}}
                      onClick={SEARCH_BY[selectedSearch].onSubmit}
                      loading={loading[selectedSearch]}
                      disabled={!watchData[selectedSearch] || !!errors[selectedSearch]?.message}
                      size="small"
                    >
                      <SearchIcon />
                    </LoadingButton>
                  </>
                ),
              }}
            />
            <FormControl variant="standard" sx={{m: 1, minWidth: 120}}>
              <InputLabel id="searchByLabel">Критерий поиска</InputLabel>
              <Select
                labelId="searchByLabel"
                id="searchBy"
                value={selectedSearch}
                onChange={(e) => {
                  if (watchData[selectedSearch]) {
                    SEARCH_BY[selectedSearch].resetSearchField();
                    SEARCH_BY[selectedSearch].onSubmit();
                  }
                  setSelectedSearch(e.target.value as keyof typeof searchString);
                }}
              >
                <MenuItem value={'serial'}>Заводской номер</MenuItem>
                <MenuItem value={'operator'}>Мобильный оператор</MenuItem>
                <MenuItem value={'modelName'}>Название модели</MenuItem>
                <MenuItem value={'createdYear'}>Год производства</MenuItem>
              </Select>
            </FormControl>
            {loadingState === 'loading' && <CircularProgress style={{marginTop: '10px'}} size="24px" />}
          </Stack>
        </Stack>
      </Stack>
    </Toolbar>
  );
};
